// src/pages/form/FormPoliciesPage.tsx
// Admin: form policies, form-type registry, entity-forms lookup — tabbed,
// mirrors CalendarSettingsPage. Replaces the generated FormSettingsPage
// scaffold. See docs/ux/form-ia.md §4.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { PoliciesSettings } from '@/features/form/PoliciesSettings';
import { FormTypesSettings } from '@/features/form/FormTypesSettings';
import { EntityFormsSettings } from '@/features/form/EntityFormsSettings';

type Tab = 'policies' | 'types' | 'entity-forms';
const TABS: Tab[] = ['policies', 'types', 'entity-forms'];

export const FormPoliciesPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'policies';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">{t('form.admin.title')}</h1>
        <Link to="/forms" className="text-sm text-primary hover:underline">{t('form.builder.backToList')}</Link>
      </div>

      <div role="tablist" aria-label={t('form.admin.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`form.admin.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'policies' && <PoliciesSettings />}
      {tab === 'types' && <FormTypesSettings />}
      {tab === 'entity-forms' && <EntityFormsSettings />}
    </div>
  );
});
